/**
 * HUD - Heads-up display showing player stats
 */

import { Player } from '../entities/Player';
import { InventoryComponent } from '../items/InventoryComponent';
import { ItemStack } from '../items/Item';
import { Vector2 } from '../math/Vector2';
import { t, Localization } from '../systems/Localization';
import { renderItemIcon, hasItemIcon } from './ItemIcons';

// Structure hover info for tooltips
export interface StructureHoverInfo {
  name: string;
  hint: string;
  screenX: number;
  screenY: number;
}

export class HUD {
  private player: Player | null = null;
  private inventory: InventoryComponent | null = null;
  private timeString: string = '12:00';
  private dayNumber: number = 1;

  // Level info
  private level: number = 0;
  private xp: number = 0;
  private xpProgress: number = 0;
  private isMaxLevel: boolean = false;

  // Drag and drop state
  private draggingSlot: number = -1;
  private dragStartPos: Vector2 = new Vector2(0, 0);
  private currentMousePos: Vector2 = new Vector2(0, 0);
  private isDragging: boolean = false;

  // Hover state for tooltip
  private hoveredSlot: number = -1;

  // Structure hover tooltip
  private hoveredStructure: StructureHoverInfo | null = null;

  // Ranged weapon info
  private rangedWeaponInfo: {
    currentAmmo: number;
    magazineSize: number;
    isReloading: boolean;
    reloadProgress: number;
    weaponName: string;
    ammoInInventory: number;
  } | null = null;

  // Hotbar layout (cached for hit detection)
  private hotbarStartX: number = 0;
  private hotbarY: number = 0;
  private slotSize: number = 50;
  private slotPadding: number = 5;
  private slotCount: number = 10;

  setPlayer(player: Player): void {
    this.player = player;
  }

  setInventory(inventory: InventoryComponent): void {
    this.inventory = inventory;
  }

  setTime(timeString: string, day: number): void {
    this.timeString = timeString;
    this.dayNumber = day;
  }

  setHoveredStructure(info: StructureHoverInfo | null): void {
    this.hoveredStructure = info;
  }

  setLevel(level: number, xp: number, progress: number, isMaxLevel: boolean): void {
    this.level = level;
    this.xp = xp;
    this.xpProgress = progress;
    this.isMaxLevel = isMaxLevel;
  }

  setRangedWeaponInfo(info: {
    currentAmmo: number;
    magazineSize: number;
    isReloading: boolean;
    reloadProgress: number;
    weaponName: string;
    ammoInInventory: number;
  } | null): void {
    this.rangedWeaponInfo = info;
  }

  /** Get which hotbar slot is at the given position, or -1 if none */
  getSlotAtPosition(pos: Vector2): number {
    for (let i = 0; i < this.slotCount; i++) {
      const x = this.hotbarStartX + i * (this.slotSize + this.slotPadding);
      if (pos.x >= x && pos.x <= x + this.slotSize &&
          pos.y >= this.hotbarY && pos.y <= this.hotbarY + this.slotSize) {
        return i;
      }
    }
    return -1;
  }

  /** Handle mouse down on HUD - returns true if handled */
  handleMouseDown(pos: Vector2): boolean {
    const slot = this.getSlotAtPosition(pos);
    if (slot >= 0 && this.inventory?.getSlot(slot)) {
      this.draggingSlot = slot;
      this.dragStartPos = pos;
      this.currentMousePos = pos;
      this.isDragging = false; // Not dragging until mouse moves
      return true;
    }
    return false;
  }

  /** Handle mouse move - returns true if dragging */
  handleMouseMove(pos: Vector2): boolean {
    this.currentMousePos = pos;

    if (this.draggingSlot >= 0) {
      // Start dragging if moved enough
      const dragThreshold = 5;
      const dist = Math.sqrt(
        Math.pow(pos.x - this.dragStartPos.x, 2) +
        Math.pow(pos.y - this.dragStartPos.y, 2)
      );
      if (dist > dragThreshold) {
        this.isDragging = true;
      }
      return this.isDragging;
    }

    // Track hovered slot for tooltip (when not dragging)
    this.hoveredSlot = this.getSlotAtPosition(pos);
    return false;
  }

  /** Handle mouse up - returns true if handled a drop */
  handleMouseUp(pos: Vector2): boolean {
    if (this.draggingSlot >= 0 && this.isDragging) {
      const targetSlot = this.getSlotAtPosition(pos);

      if (targetSlot >= 0 && targetSlot !== this.draggingSlot) {
        // Swap the slots
        this.inventory?.swapSlots(this.draggingSlot, targetSlot);
      }

      this.draggingSlot = -1;
      this.isDragging = false;
      return true;
    }

    this.draggingSlot = -1;
    this.isDragging = false;
    return false;
  }

  /** Check if currently dragging */
  isDraggingItem(): boolean {
    return this.isDragging;
  }

  render(ctx: CanvasRenderingContext2D, screenWidth: number, screenHeight: number): void {
    if (!this.player) return;

    // Health bar
    this.renderHealthBar(ctx, 20, 20);

    // Stamina bar
    this.renderStaminaBar(ctx, 20, 50);

    // Level/XP bar
    this.renderLevelBar(ctx, 20, 80);

    // Ammo display (if holding ranged weapon)
    if (this.rangedWeaponInfo) {
      this.renderAmmoDisplay(ctx, screenWidth, screenHeight);
    }

    // Time display
    this.renderTime(ctx, screenWidth - 100, 20);

    // Hotbar
    if (this.inventory) {
      this.renderHotbar(ctx, screenWidth, screenHeight);
    }

    // Mini instructions
    this.renderInstructions(ctx, screenWidth - 200, screenHeight - 120);

    // Structure hover tooltip
    if (this.hoveredStructure) {
      this.renderStructureTooltip(ctx);
    }
  }

  private renderHealthBar(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const width = 200;
    const height = 20;
    const health = this.player!.health;

    // Background
    ctx.fillStyle = '#333';
    ctx.fillRect(x, y, width, height);

    // Health fill
    const healthPercent = health.getHealthPercent();
    const healthColor = healthPercent > 0.5 ? '#4ecca3' : healthPercent > 0.25 ? '#f39c12' : '#e74c3c';
    ctx.fillStyle = healthColor;
    ctx.fillRect(x, y, width * healthPercent, height);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, width, height);

    // Text
    ctx.fillStyle = '#fff';
    ctx.font = '12px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(
      `${t('hud_hp')}: ${Math.ceil(health.getCurrentHealth())}/${health.getMaxHealth()}`,
      x + width / 2,
      y + 15
    );
  }

  private renderStaminaBar(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const width = 200;
    const height = 15;
    const stamina = this.player!.stamina;

    // Background
    ctx.fillStyle = '#333';
    ctx.fillRect(x, y, width, height);

    // Stamina fill
    ctx.fillStyle = '#f1c40f';
    ctx.fillRect(x, y, width * stamina.getStaminaPercent(), height);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);

    // Text
    ctx.fillStyle = '#fff';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(
      `${t('hud_sp')}: ${Math.ceil(stamina.getCurrentStamina())}/${stamina.getMaxStamina()}`,
      x + width / 2,
      y + 11
    );
  }

  private renderLevelBar(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const width = 200;
    const height = 15;

    // Background
    ctx.fillStyle = '#333';
    ctx.fillRect(x, y, width, height);

    // XP fill (purple/violet color)
    ctx.fillStyle = '#9b59b6';
    ctx.fillRect(x, y, width * this.xpProgress, height);

    // Border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);

    // Text - show level and XP
    ctx.fillStyle = '#fff';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    const levelText = this.isMaxLevel
      ? `Lv.${this.level} MAX`
      : `Lv.${this.level} - ${this.xp} XP`;
    ctx.fillText(levelText, x + width / 2, y + 11);
  }

  private renderAmmoDisplay(ctx: CanvasRenderingContext2D, _screenWidth: number, screenHeight: number): void {
    if (!this.rangedWeaponInfo) return;

    const { currentAmmo, magazineSize, isReloading, reloadProgress, weaponName, ammoInInventory } = this.rangedWeaponInfo;

    // Position on the left side of the screen
    const displayWidth = 120;
    const displayHeight = 60;
    const x = 20;
    const y = screenHeight - this.slotSize - 20 - displayHeight - 10;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.9)';
    ctx.fillRect(x, y, displayWidth, displayHeight);

    // Border (red if empty, orange if low, green if good)
    const ammoPercent = currentAmmo / magazineSize;
    let borderColor = '#4ecca3'; // Green
    if (currentAmmo === 0) {
      borderColor = '#e74c3c'; // Red
    } else if (ammoPercent <= 0.3) {
      borderColor = '#f39c12'; // Orange
    }
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, displayWidth, displayHeight);

    // Weapon name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(weaponName, x + displayWidth / 2, y + 14);

    if (isReloading) {
      // Reload progress bar
      const barY = y + 22;
      const barHeight = 12;
      const barPadding = 8;

      // Background
      ctx.fillStyle = '#333';
      ctx.fillRect(x + barPadding, barY, displayWidth - barPadding * 2, barHeight);

      // Progress fill (animated color)
      const hue = 30 + reloadProgress * 90; // Orange to green
      ctx.fillStyle = `hsl(${hue}, 70%, 50%)`;
      ctx.fillRect(x + barPadding, barY, (displayWidth - barPadding * 2) * reloadProgress, barHeight);

      // Border
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 1;
      ctx.strokeRect(x + barPadding, barY, displayWidth - barPadding * 2, barHeight);

      // Reloading text
      ctx.fillStyle = '#f39c12';
      ctx.font = 'bold 12px monospace';
      ctx.fillText('RELOADING...', x + displayWidth / 2, y + 50);
    } else {
      // Ammo count - big numbers
      ctx.fillStyle = currentAmmo === 0 ? '#e74c3c' : '#fff';
      ctx.font = 'bold 20px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`${currentAmmo} / ${magazineSize}`, x + displayWidth / 2, y + 38);

      // Reserve ammo (smaller, below)
      ctx.fillStyle = '#888';
      ctx.font = '11px monospace';
      ctx.fillText(`Reserve: ${ammoInInventory}`, x + displayWidth / 2, y + 54);
    }

    // Draw ammo bullets visual indicator
    const bulletSize = 4;
    const bulletSpacing = 2;
    const bulletsPerRow = Math.min(10, magazineSize);
    const rows = Math.ceil(magazineSize / bulletsPerRow);
    const bulletAreaWidth = bulletsPerRow * (bulletSize + bulletSpacing);
    const bulletStartX = x + (displayWidth - bulletAreaWidth) / 2;
    const bulletStartY = y + displayHeight - 8 - rows * (bulletSize + bulletSpacing);

    // Only show bullet indicators for smaller magazines
    if (magazineSize <= 20 && !isReloading) {
      for (let i = 0; i < magazineSize; i++) {
        const row = Math.floor(i / bulletsPerRow);
        const col = i % bulletsPerRow;
        const bx = bulletStartX + col * (bulletSize + bulletSpacing);
        const by = bulletStartY + row * (bulletSize + bulletSpacing);

        if (i < currentAmmo) {
          ctx.fillStyle = '#f1c40f'; // Gold for loaded
        } else {
          ctx.fillStyle = '#333'; // Dark for empty
        }
        ctx.fillRect(bx, by, bulletSize, bulletSize);
      }
    }
  }

  private renderTime(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(x, y, 80, 40);

    ctx.fillStyle = '#fff';
    ctx.font = '16px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(this.timeString, x + 40, y + 18);

    ctx.font = '12px monospace';
    ctx.fillStyle = '#aaa';
    ctx.fillText(`${t('hud_day')} ${this.dayNumber}`, x + 40, y + 34);
  }

  private renderHotbar(ctx: CanvasRenderingContext2D, screenWidth: number, screenHeight: number): void {
    const totalWidth = this.slotCount * (this.slotSize + this.slotPadding) - this.slotPadding;
    this.hotbarStartX = (screenWidth - totalWidth) / 2;
    this.hotbarY = screenHeight - this.slotSize - 20;

    const selectedSlot = this.inventory!.getSelectedSlot();
    const hoverSlot = this.isDragging ? this.getSlotAtPosition(this.currentMousePos) : -1;

    for (let i = 0; i < this.slotCount; i++) {
      const x = this.hotbarStartX + i * (this.slotSize + this.slotPadding);
      const isSelected = i === selectedSlot;
      const isHovered = i === hoverSlot;
      const isBeingDragged = i === this.draggingSlot && this.isDragging;
      const slot = this.inventory!.getSlot(i);

      // Slot background
      if (isHovered && this.isDragging) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.7)';
      } else if (isSelected) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.5)';
      } else {
        ctx.fillStyle = 'rgba(30, 30, 50, 0.8)';
      }
      ctx.fillRect(x, this.hotbarY, this.slotSize, this.slotSize);

      // Border
      if (isHovered && this.isDragging) {
        ctx.strokeStyle = '#7dffcc';
        ctx.lineWidth = 3;
      } else if (isSelected) {
        ctx.strokeStyle = '#4ecca3';
        ctx.lineWidth = 3;
      } else {
        ctx.strokeStyle = '#666';
        ctx.lineWidth = 1;
      }
      ctx.strokeRect(x, this.hotbarY, this.slotSize, this.slotSize);

      // Item (dimmed if being dragged)
      if (slot && !isBeingDragged) {
        this.renderItemSlot(ctx, x, this.hotbarY, this.slotSize, slot);
      } else if (slot && isBeingDragged) {
        // Render dimmed version in original slot
        ctx.globalAlpha = 0.3;
        this.renderItemSlot(ctx, x, this.hotbarY, this.slotSize, slot);
        ctx.globalAlpha = 1.0;
      }

      // Slot number
      ctx.fillStyle = '#888';
      ctx.font = '10px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`${(i + 1) % 10}`, x + 3, this.hotbarY + 12);
    }

    // Render dragged item at cursor
    if (this.isDragging && this.draggingSlot >= 0) {
      const draggedItem = this.inventory!.getSlot(this.draggingSlot);
      if (draggedItem) {
        const dragX = this.currentMousePos.x - this.slotSize / 2;
        const dragY = this.currentMousePos.y - this.slotSize / 2;

        // Shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(dragX + 4, dragY + 4, this.slotSize, this.slotSize);

        // Item background
        ctx.fillStyle = 'rgba(30, 30, 50, 0.95)';
        ctx.fillRect(dragX, dragY, this.slotSize, this.slotSize);
        ctx.strokeStyle = '#4ecca3';
        ctx.lineWidth = 2;
        ctx.strokeRect(dragX, dragY, this.slotSize, this.slotSize);

        this.renderItemSlot(ctx, dragX, dragY, this.slotSize, draggedItem);
      }
    }

    // Render tooltip for hovered item (when not dragging)
    if (!this.isDragging && this.hoveredSlot >= 0) {
      const slot = this.inventory!.getSlot(this.hoveredSlot);
      if (slot) {
        this.renderTooltip(ctx, slot);
      }
    }
  }

  private renderTooltip(ctx: CanvasRenderingContext2D, slot: ItemStack): void {
    const tooltipWidth = 180;
    const tooltipHeight = 80;

    // Position tooltip above the hotbar
    const slotX = this.hotbarStartX + this.hoveredSlot * (this.slotSize + this.slotPadding);
    let tooltipX = slotX - (tooltipWidth - this.slotSize) / 2;
    const tooltipY = this.hotbarY - tooltipHeight - 10;

    // Keep tooltip on screen
    if (tooltipX < 10) tooltipX = 10;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    ctx.strokeStyle = '#4ecca3';
    ctx.lineWidth = 1;
    ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Get localized item info
    const itemInfo = Localization.getItem(slot.item.id);
    const itemName = itemInfo?.name || slot.item.name;
    const itemDesc = itemInfo?.description || slot.item.description;

    // Item name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(itemName, tooltipX + 10, tooltipY + 20);

    // Item type
    ctx.fillStyle = slot.item.getColor();
    ctx.font = '11px monospace';
    ctx.fillText(slot.item.type, tooltipX + 10, tooltipY + 35);

    // Description
    ctx.fillStyle = '#aaa';
    ctx.font = '11px monospace';
    const maxChars = 25;
    if (itemDesc.length > maxChars) {
      ctx.fillText(itemDesc.substring(0, maxChars) + '...', tooltipX + 10, tooltipY + 55);
    } else {
      ctx.fillText(itemDesc, tooltipX + 10, tooltipY + 55);
    }

    // Value
    ctx.fillStyle = '#f1c40f';
    ctx.fillText(`${t('tooltip_value')}: ${slot.item.value}`, tooltipX + 10, tooltipY + 70);
  }

  private renderItemSlot(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, slot: ItemStack): void {
    const iconSize = size - 16;
    const iconX = x + 8;
    const iconY = y + 8;

    // Try to render pixel art icon first
    if (hasItemIcon(slot.item.id)) {
      renderItemIcon(ctx, slot.item.id, iconX, iconY, iconSize);
    } else {
      // Fallback to colored square with letter
      ctx.fillStyle = slot.item.getColor();
      ctx.fillRect(iconX, iconY, iconSize, iconSize);

      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(iconX, iconY, iconSize, iconSize);

      // Item initial
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(slot.item.name.charAt(0), x + size / 2, y + size / 2 + 5);
    }

    // Quantity
    if (slot.quantity > 1) {
      // Background for better visibility
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(x + size - 20, y + size - 16, 18, 14);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${slot.quantity}`, x + size - 4, y + size - 4);
    }
  }

  private renderInstructions(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(x, y, 200, 120);

    ctx.fillStyle = '#888';
    ctx.font = '11px monospace';
    ctx.textAlign = 'left';

    const instructions = [
      t('instr_move'),
      t('instr_attack'),
      t('instr_inventory'),
      t('instr_crafting'),
      t('instr_build'),
      t('instr_hotbar'),
      t('instr_use'),
      t('instr_zoom')
    ];

    instructions.forEach((text, i) => {
      ctx.fillText(text, x + 10, y + 15 + i * 14);
    });
  }

  private renderStructureTooltip(ctx: CanvasRenderingContext2D): void {
    if (!this.hoveredStructure) return;

    const { name, hint, screenX, screenY } = this.hoveredStructure;

    // Tooltip dimensions
    const padding = 10;
    const lineHeight = 18;
    ctx.font = 'bold 13px monospace';
    const nameWidth = ctx.measureText(name).width;
    ctx.font = '11px monospace';
    const hintWidth = ctx.measureText(hint).width;
    ctx.font = '10px monospace';
    const pressEWidth = ctx.measureText(t('hint_press_e')).width;

    const tooltipWidth = Math.max(nameWidth, hintWidth, pressEWidth) + padding * 2;
    const tooltipHeight = lineHeight * 3 + padding * 2;

    // Position tooltip above the structure
    let tooltipX = screenX - tooltipWidth / 2;
    let tooltipY = screenY - tooltipHeight - 20;

    // Keep on screen
    if (tooltipX < 10) tooltipX = 10;
    if (tooltipY < 10) tooltipY = screenY + 30; // Below if too high

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Border
    ctx.strokeStyle = '#4ecca3';
    ctx.lineWidth = 2;
    ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Structure name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 13px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(name, tooltipX + padding, tooltipY + padding + 12);

    // Hint text
    ctx.fillStyle = '#aaa';
    ctx.font = '11px monospace';
    ctx.fillText(hint, tooltipX + padding, tooltipY + padding + 12 + lineHeight);

    // Press E hint
    ctx.fillStyle = '#4ecca3';
    ctx.font = '10px monospace';
    ctx.fillText(t('hint_press_e'), tooltipX + padding, tooltipY + padding + 12 + lineHeight * 2);
  }
}
